import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useAudit } from "./AuditContext";
import { tapFeedback } from "./haptics";
import { PresenterFloorShadow } from "./PresenterFloorShadow";
import { useIsMobileAudit } from "./useIsMobileAudit";

/**
 * Rehearsal Room · Room 06 — OPENING
 * ==================================
 * Presenter stands to the LEFT of the board. The board itself becomes the
 * interaction surface — 6 opening styles as selectable cards, then a 5-way
 * reflection. No floating panels.
 */

const EASE = [0.16, 1, 0.3, 1];
const BASE_PRESENTER_HEIGHT = 110;
const EMPTY = Object.freeze([]);

const INTRO_STEPS = [
  "The opening sets the relationship before it delivers the content.",
  "Choose the kind of opening you would most naturally use.",
  "There\u2019s no perfect answer. Follow your instinct.",
];

const OPENING_CHOICES = [
  {
    id: "welcome",
    heading: "Welcome & Introduce Myself",
    line: "Good morning. I\u2019m Alex, and today I\u2019d like to talk with you about something that matters to all of us.",
  },
  {
    id: "curiosity",
    heading: "Curiosity",
    line: "What if the way we usually think about this is only part of the story?",
  },
  {
    id: "relevance",
    heading: "Relevance",
    line: "What we\u2019re going to look at today has a direct impact on what happens next.",
  },
  {
    id: "tension",
    heading: "Tension / Challenge",
    line: "We\u2019re facing a choice, and doing nothing is also a decision.",
  },
  {
    id: "story",
    heading: "Story",
    line: "Not long ago, something happened that completely changed the way I looked at this.",
  },
  {
    id: "bold_claim",
    heading: "Bold Claim",
    line: "The biggest obstacle may not be the one we think it is.",
  },
];

const POST_SELECT_BUBBLE =
  "Good. The way you open tells me what kind of relationship you instinctively build first.";

const REFLECTION_QUESTION = "What matters most to you in your opening?";

const REFLECTION_OPTIONS = [
  { id: "clarity",     label: "Clarity first" },
  { id: "connection",  label: "Connection first" },
  { id: "interest",    label: "Interest first" },
  { id: "credibility", label: "Credibility first" },
  { id: "momentum",    label: "Momentum first" },
];

const POST_REFLECTION_BUBBLE =
  "That\u2019s useful. Your opening is not just a sentence — it reflects what you try to create first in the room.";

// Presenter anchor positions (viewport %).
// Presenter enters from OFF-STAGE LEFT walking to the RIGHT into position.
// footY > 100 pushes his feet BELOW the viewport so the head reads clearly
// while he stands tall in the frame.
const OFFSTAGE_ENTRY = { cx: -12, footY: 112, scale: 1.0 };
const STAGE_LEFT     = { cx: 15,  footY: 112, scale: 1.0 };
const STAGE_LEFT_S   = { cx: 15,  footY: 110, scale: 0.72 }; // smaller while listening
const OFFSTAGE_LEFT  = { cx: -12, footY: 112, scale: 1.0 };

// `height` was originally a fixed `960 * scale` px, tuned against a
// ~900px-tall desktop viewport (960/900 ≈ 106.7%). Expressed as that same
// ratio of the room's own full height, it reproduces the identical
// desktop composition while scaling correctly on any viewport height
// (see RehearsalRoom.jsx's identical fix for the full calibration note).
// The `-40px` foot-drop correction is converted the same way (40/900).
const anchorToStyle = (a) => ({
  left:   `${a.cx}%`,
  bottom: `${100 - a.footY - 4.44}%`,
  height: `${106.7 * a.scale}%`,
});

// ---------- COMPONENT ------------------------------------------------------
export function RehearsalRoomOpening() {
  const {
    answers,
    setRoomAnswer,
    setTagsForRoom,
    setCanProceed,
    setOverrideMessage,
    setBeforeNext,
    setReflection,
    selectedPresenter,
    overrideMessage,
    next,
  } = useAudit();

  const a = answers.opening || {};
  const introStep        = a.introStep        ?? 0;
  const selected         = a.selected         ?? null;
  const postSelectSeen   = a.postSelectSeen   === true;
  const reflectionPick   = a.reflection?.selected ?? null;
  const postReflectSeen  = a.postReflectSeen  === true;

  // Local entry/exit choreography.
  const [entered, setEntered] = useState(true);
  const [exiting, setExiting] = useState(false);
  const [pose, setPose] = useState("welcomeOpen");
  const isMobile = useIsMobileAudit();
  const [flip, setFlip] = useState(false);

  // ---------- Phase flags --------------------------------------------------
  const introDone         = introStep >= INTRO_STEPS.length;
  const inIntro           = entered && !introDone;
  const inTaskPick        = entered && introDone && !selected;
  const inPostSelect      = entered && introDone && !!selected && !postSelectSeen;
  const inReflectionPick  = entered && postSelectSeen && !reflectionPick;
  const inPostReflect     = entered && postSelectSeen && !!reflectionPick && !postReflectSeen;
  const isFinished        = entered && postReflectSeen;

  const inCatcher = inIntro || inPostSelect || inPostReflect;

  // ---------- Entry (skip walk-in — presenter stands ready) ---------------
  // Presenter enters already in welcomeOpen pose at his STAGE_LEFT position,
  // facing the board. No walking animation on entry.

  // ---------- Message + Continue-gate sync ---------------------------------
  useEffect(() => {
    if (!entered)           { setOverrideMessage("");                                    setCanProceed(false); return; }
    if (inIntro)            { setOverrideMessage(INTRO_STEPS[introStep]);                setCanProceed(false); return; }
    if (inTaskPick)         { setOverrideMessage("");                                    setCanProceed(false); return; }
    if (inPostSelect)       { setOverrideMessage(POST_SELECT_BUBBLE);                    setCanProceed(false); return; }
    if (inReflectionPick)   { setOverrideMessage(REFLECTION_QUESTION);                   setCanProceed(false); return; }
    if (inPostReflect)      { setOverrideMessage(POST_REFLECTION_BUBBLE);                setCanProceed(false); return; }
    // Journey complete.
    setOverrideMessage(POST_REFLECTION_BUBBLE);
    setCanProceed(true);
  }, [entered, inIntro, introStep, inTaskPick, inPostSelect, inReflectionPick, inPostReflect,
      setOverrideMessage, setCanProceed]);

  // ---------- Pose per phase -----------------------------------------------
  useEffect(() => {
    if (!entered || exiting) return;
    // Standing states in Room 06 — during both ACTION phases (task pick +
    // reflection pick) the presenter shrinks into `listening` and is flipped
    // so he faces the board/task area on the right. When he speaks again he
    // returns to the larger presentingAlt in its natural (unflipped)
    // orientation. welcomeOpen naturally faces left, so it keeps its flip.
    if (inIntro)          { setPose("welcomeOpen"); setFlip(true);  return; }
    if (inTaskPick)       { setPose("listening");   setFlip(true);  return; }
    if (inPostSelect)     { setPose("presentingAlt"); setFlip(false); return; }
    if (inReflectionPick) { setPose("listening");   setFlip(true);  return; }
    if (inPostReflect)    { setPose("presentingAlt"); setFlip(false); return; }
    if (isFinished)       { setPose("presentingAlt"); setFlip(false); return; }
  }, [entered, exiting, inIntro, inTaskPick, inPostSelect, inReflectionPick, inPostReflect, isFinished]);

  // ---------- Tags ---------------------------------------------------------
  useEffect(() => {
    setTagsForRoom("opening", selected ? [`opening:${selected}`] : EMPTY);
  }, [selected, setTagsForRoom]);

  useEffect(() => {
    setTagsForRoom(
      "opening_reflection",
      reflectionPick ? [`opening_reflection:${reflectionPick}`] : EMPTY,
    );
  }, [reflectionPick, setTagsForRoom]);

  // ---------- Continue interceptor (walking exit) --------------------------
  useEffect(() => {
    setBeforeNext(() => {
      if (!isFinished) return false;
      setPose("walking");
      setFlip(true);
      setExiting(true);
      setTimeout(() => { setBeforeNext(null); next(); }, 1200);
      return false;
    });
    return () => setBeforeNext(null);
  }, [isFinished, setBeforeNext, next]);

  // ---------- Handlers ------------------------------------------------------
  const advanceCatcher = () => {
    if (inIntro)         return setRoomAnswer("opening", { introStep: introStep + 1 });
    if (inPostSelect)    return setRoomAnswer("opening", { postSelectSeen: true });
    if (inPostReflect)   return setRoomAnswer("opening", { postReflectSeen: true });
  };

  const pickCard = (id) => {
    if (!inTaskPick) return;
    tapFeedback();
    setRoomAnswer("opening", { selected: id, postSelectSeen: false });
  };

  const pickReflection = (id) => {
    if (!inReflectionPick) return;
    tapFeedback();
    const tag = `opening_reflection:${id}`;
    setRoomAnswer("opening", {
      reflection: { selected: id, tag },
      postReflectSeen: false,
    });
    setReflection("opening", {
      question: REFLECTION_QUESTION,
      selected: id,
      tag,
    });
  };

  // ---------- Presenter target ---------------------------------------------
  const targetAnchor = (() => {
    if (exiting)     return OFFSTAGE_LEFT;
    if (!entered)    return OFFSTAGE_ENTRY;
    if (inTaskPick || inReflectionPick)  return STAGE_LEFT_S;
    return STAGE_LEFT;
  })();

  const presenter = selectedPresenter;
  const poseFile = presenter?.poses?.[pose] || presenter?.poses?.neutral;
  const presenterSrc = presenter ? `${presenter.avatarBase}/${poseFile}` : "";

  // ---------- Bubble anchor (follows presenter) ----------------------------
  const _bubbleAnchor = (() => {
    const cx = targetAnchor.cx;
    const headYPct = Math.max(3, targetAnchor.footY - BASE_PRESENTER_HEIGHT * targetAnchor.scale - 1);
    const preferLeft = cx > 30;
    const gap = 4;
    return {
      top: headYPct,
      left: preferLeft ? cx - gap : cx + gap,
      preferLeft,
    };
  })();
  const bubbleStyle = {
    top: `${_bubbleAnchor.top}%`,
    left: `${_bubbleAnchor.left}%`,
    transform: _bubbleAnchor.preferLeft ? "translateX(-100%)" : "translateX(0)",
    zIndex: 100,
    maxWidth: 360,
  };
  const _tailStyle = _bubbleAnchor.preferLeft
    ? { right: 24, bottom: -16, left: "auto" }
    : { left: 40, bottom: -16, right: "auto" };

  const showMainBubble = !!overrideMessage && !inTaskPick && !inReflectionPick;

  const catcherStep = inIntro       ? introStep + 1
                    : inPostSelect  ? 1
                    : inPostReflect ? 1 : 1;
  const catcherTotal = inIntro       ? INTRO_STEPS.length
                     : 1;

  // ---------- Board interaction surface ------------------------------------
  // Baked board area in /audit/bg_opening.webp (approx, cream inner cream area).
  const BOARD_STYLE = {
    // outer position on the wall
    left: "27%",
    top: "16%",
    width: "70%",
    height: "63%",
  };

  return (
    <div className="absolute inset-0 overflow-hidden" data-testid="rehearsal-room-opening" style={{ background: "#141310" }}>
      {/* Background */}
      <div aria-hidden="true" className="absolute inset-0"
        style={{ backgroundImage: "url(/audit/bg_opening.webp)", backgroundSize: "cover", backgroundPosition: "center" }} />

      {/* Subtle scrim */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none"
        style={{ background: "rgba(10,8,5,0.15)", zIndex: 5 }} />

      {/* ---------- Board (interaction surface) --------------------------- */}
      <div
        className="absolute"
        style={{ ...BOARD_STYLE, zIndex: 15 }}
        data-testid="opening-board"
      >
        <AnimatePresence mode="wait">
          {/* Opening choice cards */}
          {(inTaskPick || inPostSelect) && (
            <motion.div
              key="opening-cards"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.45, ease: EASE }}
              className="w-full h-full p-6 grid grid-cols-2 gap-4 content-center"
              data-testid="opening-cards"
            >
              {OPENING_CHOICES.map((choice) => {
                const isSelected = selected === choice.id;
                return (
                  <button
                    key={choice.id}
                    type="button"
                    onClick={() => pickCard(choice.id)}
                    disabled={!inTaskPick}
                    data-testid={`opening-choice-${choice.id}`}
                    className="opening-card group text-left p-5 transition-all duration-200 hover:-translate-y-0.5 disabled:cursor-default"
                    style={{
                      background: isSelected
                        ? "linear-gradient(180deg, #E9C88A 0%, #B9832C 100%)"
                        : "rgba(255, 249, 234, 0.65)",
                      border: isSelected
                        ? "2px solid #c25a3a"
                        : "1px solid rgba(90,60,25,0.14)",
                      boxShadow: isSelected
                        ? "0 14px 32px rgba(144,83,59,0.35)"
                        : "0 3px 10px rgba(0,0,0,0.06)",
                      color: "#1B1B18",
                      borderRadius: 14,
                    }}
                  >
                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-terracotta mb-1.5">
                      {choice.heading}
                    </p>
                    <p className="font-serif text-[15px] leading-snug italic">
                      &ldquo;{choice.line}&rdquo;
                    </p>
                  </button>
                );
              })}
            </motion.div>
          )}

          {/* Reflection cards */}
          {(inReflectionPick || inPostReflect || isFinished) && (
            <motion.div
              key="opening-reflection"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.45, ease: EASE }}
              className="w-full h-full p-8 flex flex-col justify-center gap-6"
              data-testid="opening-reflection"
            >
              <p className="font-serif text-2xl md:text-3xl text-[#2b1e10] text-center leading-snug">
                {REFLECTION_QUESTION}
              </p>
              <div className="grid grid-cols-5 gap-3">
                {REFLECTION_OPTIONS.map((opt) => {
                  const sel = reflectionPick === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => pickReflection(opt.id)}
                      disabled={!inReflectionPick}
                      data-testid={`opening-reflection-${opt.id}`}
                      className="text-center px-3 py-4 transition-all duration-200 hover:-translate-y-0.5 disabled:cursor-default"
                      style={{
                        background: sel
                          ? "linear-gradient(180deg, #E9C88A 0%, #B9832C 100%)"
                          : "rgba(255, 249, 234, 0.7)",
                        border: sel
                          ? "2px solid #c25a3a"
                          : "1px solid rgba(90,60,25,0.15)",
                        boxShadow: sel
                          ? "0 12px 28px rgba(144,83,59,0.32)"
                          : "0 3px 10px rgba(0,0,0,0.05)",
                        color: "#1B1B18",
                        borderRadius: 12,
                      }}
                    >
                      <p className="font-serif text-base md:text-lg leading-snug">
                        {opt.label}
                      </p>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Mobile-only scroll hint — the 6 opening cards now stack in one
          scrollable column (see audit-mobile.css), so a small bouncing
          arrow signals there's more to see below the fold. Only shown
          while the cards themselves are on screen. */}
      {isMobile && (inTaskPick || inPostSelect) && (
        <motion.div
          className="absolute left-1/2 flex flex-col items-center gap-0.5 pointer-events-none"
          style={{ bottom: 78, transform: "translateX(-50%)", zIndex: 20 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, y: [0, 5, 0] }}
          transition={{ opacity: { duration: 0.4 }, y: { duration: 1.3, repeat: Infinity, ease: "easeInOut" } }}
          data-testid="opening-scroll-hint"
        >
          <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-paper/85 drop-shadow-[0_1px_3px_rgba(0,0,0,0.6)]">
            swipe for more
          </p>
          <ChevronDown size={16} className="text-paper/85" style={{ filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.6))" }} />
        </motion.div>
      )}

      {/* ---------- Presenter --------------------------------------------- */}
      {presenter && (
        <motion.div
          className="absolute"
          initial={false}
          animate={{
            left:   anchorToStyle(targetAnchor).left,
            bottom: anchorToStyle(targetAnchor).bottom,
            height: anchorToStyle(targetAnchor).height,
            opacity: exiting ? 0 : 1,
          }}
          transition={{ duration: exiting ? 1.1 : 1.2, ease: EASE }}
          style={{
            zIndex: 30,
            transform: "translateX(-50%)",
            aspectRatio: "1122 / 1402",
            pointerEvents: "none",
          }}
          data-testid="opening-presenter"
        >
          <PresenterFloorShadow />
          <img
            src={presenterSrc}
            alt={presenter.name}
            draggable={false}
            loading="eager"
            decoding="async"
            fetchPriority="high"
            className="select-none block"
            style={{
              height: "100%",
              width: "100%",
              objectFit: "contain",
              transform: `scaleX(${flip ? -1 : 1})`,
              transition: "transform 350ms ease",
              filter: "brightness(0.95) sepia(0.1)",
              WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, black 5%, black 95%, transparent 100%)",
              maskImage:       "linear-gradient(to bottom, transparent 0%, black 5%, black 95%, transparent 100%)",
            }}
            data-testid={`opening-presenter-img-${pose}`}
          />
        </motion.div>
      )}

      {/* ---------- Corner brand mark ------------------------------------- */}
      <div className="absolute right-6 top-5 flex flex-col items-end pointer-events-none" style={{ zIndex: 50 }}>
        <p className="font-serif italic text-paper text-lg md:text-2xl leading-none tracking-wide drop-shadow-[0_2px_6px_rgba(0,0,0,0.55)]">
          InterAct Globally
        </p>
        <p className="font-hand text-mustard text-base md:text-xl leading-tight mt-1 drop-shadow-[0_2px_6px_rgba(0,0,0,0.55)]">
          the opening room
        </p>
      </div>

      {/* ---------- Speech bubble (follows presenter) --------------------- */}
      <AnimatePresence initial={false}>
        {showMainBubble && (
          <motion.div
            key={overrideMessage}
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="absolute"
            style={bubbleStyle}
            data-testid="opening-bubble"
          >
            <div className="relative bg-white text-[#1B1B18] px-6 py-4 shadow-[0_16px_40px_rgba(0,0,0,0.4)]" style={{ borderRadius: 26 }}>
              <p className="font-serif text-lg leading-snug">{overrideMessage}</p>
              <span aria-hidden="true" className="absolute"
                style={{ ..._tailStyle, width: 0, height: 0,
                  borderLeft: "16px solid transparent", borderRight: "10px solid transparent",
                  borderTop: "20px solid #ffffff", filter: "drop-shadow(0 4px 4px rgba(0,0,0,0.15))" }} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ---------- Tap-to-continue catcher ------------------------------- */}
      {inCatcher && (
        <button
          type="button"
          onClick={advanceCatcher}
          data-testid="opening-advance"
          aria-label="Continue"
          className="absolute inset-0 cursor-pointer bg-transparent"
          style={{ zIndex: 88, transform: "scaleX(-1)" }}
        >
          <span aria-hidden="true"
            className="absolute bottom-24 left-1/2 -translate-x-1/2 font-mono text-[10px] uppercase tracking-[0.28em] text-paper/70 animate-pulse">
            tap to continue &nbsp;·&nbsp; {catcherStep} / {catcherTotal}
          </span>
        </button>
      )}

      {/* Card hover styling (uses #da983c per the audit palette) */}
      <style>{`
        .opening-card:not([disabled]):hover {
          background: rgba(218, 152, 60, 0.22) !important;
          border-color: rgba(218, 152, 60, 0.65) !important;
        }
      `}</style>
    </div>
  );
}

export default RehearsalRoomOpening;
